import {
  GoogleAuthProvider,
  User,
  getRedirectResult,
  linkWithRedirect,
  onAuthStateChanged,
  signInAnonymously,
  signInWithCredential,
  signInWithRedirect,
} from 'firebase/auth';

import { trackSignOut, trackSignUp } from './analytics/analytics';
import { AnalyticsEvents } from './analytics/events';
import { trackSentryError } from './analytics/sentry';
import { finalizeCampaignCreation, register } from './api';
import { firebaseAuth } from './firebaseUtils';
import {
  clearRegistrationCategory,
  getRegistrationCategory,
  setRegistrationCategory,
} from './localStorageUtils';
import { store } from './store';

// Global flag to ensure getRedirectResult is only called once
let redirectResultProcessed = false;
let redirectResultPromise: Promise<User | null> | null = null;

export async function signOutUser(location: 'header' | 'profile_screen') {
  const auth = await getFirebaseAuthAsync();
  const currentUser = auth.currentUser;

  // Track sign out event before actually signing out
  if (currentUser?.uid) {
    try {
      await trackSignOut(currentUser.uid, location);
    } catch (error) {
      // Log error but don't prevent sign out
      trackSentryError('Failed to track sign out event', {
        userId: currentUser.uid,
        error,
      });
      console.error('[Auth] Failed to track sign out event:', error);
    }
  }

  await auth.signOut();
  window.location.reload();
}

export function isUserSignedIn(user?: User | null) {
  return !!user;
}

export function isUserAnonymous(user?: User | null) {
  return user?.isAnonymous;
}

export function isUserRegistered(user?: User | null) {
  return !!(user && user.email);
}

async function isUserSignedInAsync() {
  const auth = await getFirebaseAuthAsync();
  return isUserSignedIn(auth.currentUser);
}

async function isUserAnonymousAsync() {
  const auth = await getFirebaseAuthAsync();
  return isUserAnonymous(auth.currentUser);
}

export async function isUserRegisteredAsync() {
  const auth = await getFirebaseAuthAsync();
  return isUserRegistered(auth.currentUser);
}

export async function getCurrentUserAsync() {
  const auth = await getFirebaseAuthAsync();
  return auth.currentUser;
}

// Modified getFirebaseAuthAsync to not interfere with redirect result
async function getFirebaseAuthAsync() {
  // Don't wait for auth state if we're processing redirect result
  if (!redirectResultProcessed) {
    return firebaseAuth; // Return immediately if redirect is not yet processed
  }

  // Standard auth state ready promise
  await new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      firebaseAuth,
      (user) => {
        resolve(user);
        unsubscribe();
      },
      (error) => {
        trackSentryError('Error getting Firebase auth async', { error });
        reject(error);
      }
    );
  });
  return firebaseAuth;
}

export async function registerAsync(category: string) {
  setRegistrationCategory(category);
  const auth = await getFirebaseAuthAsync();
  const googleProvider = new GoogleAuthProvider();

  googleProvider.setCustomParameters({
    prompt: 'select_account',
  });

  try {
    const currentUser = auth.currentUser;

    if (currentUser && currentUser.isAnonymous) {
      await linkWithRedirect(currentUser, googleProvider);
    } else if (!currentUser) {
      await signInWithRedirect(auth, googleProvider);
    } else {
      // User is already signed in with a non-anonymous account
      return currentUser;
    }
    // Note: After redirect, the user will return to the app and we need to handle
    // the result in handleRedirectResult() function
  } catch (error: any) {
    trackSentryError('❌ registerAsync: error', { error });
    console.error('❌ registerAsync: error', error);
    throw error;
  }
}

// Improved function to handle the redirect result when user returns from authentication
export async function handleRedirectResult() {
  // Ensure we only process redirect result once per page load
  if (redirectResultProcessed && !redirectResultPromise) {
    return null;
  }

  // If already processing, return the existing promise
  if (redirectResultPromise) {
    return redirectResultPromise;
  }

  // Create the promise and mark as processing
  redirectResultPromise = processRedirectResult();
  return redirectResultPromise;
}

async function processRedirectResult(): Promise<User | null> {
  try {
    // Get auth instance WITHOUT triggering any other auth operations
    const auth = firebaseAuth;

    // This MUST be called first, before any other Firebase auth operations
    const result = await getRedirectResult(auth);
    redirectResultProcessed = true;

    if (result && result.user) {
      const userId = result.user.uid;
      await register(userId);
      const category =
        getRegistrationCategory() || AnalyticsEvents.SIGN_UP.values.CATEGORY.FALLBACK;
      trackSignUp(userId, category);
      clearRegistrationCategory();

      const state = store.getState().appState;
      const selectedCampaign = state.selectedCampaign;
      const session = state.session;

      if (selectedCampaign?.id) {
        await finalizeCampaignCreation(userId, selectedCampaign.id, session);
      }

      return result.user;
    }

    return null;
  } catch (error: any) {
    redirectResultProcessed = true;

    if (error.code === 'auth/credential-already-in-use') {
      const { oauthIdToken, oauthAccessToken } = error.customData._tokenResponse;
      const credential = GoogleAuthProvider.credential(oauthIdToken, oauthAccessToken);
      const auth = await getFirebaseAuthAsync(); // Safe to call here, redirect already processed
      const userCredential = await signInWithCredential(auth, credential);
      const userId = userCredential.user.uid;
      await register(userId);
      const category =
        getRegistrationCategory() || AnalyticsEvents.SIGN_UP.values.CATEGORY.FALLBACK;
      trackSignUp(userId, category);
      clearRegistrationCategory();

      const state = store.getState().appState;
      const selectedCampaign = state.selectedCampaign;
      const session = state.session;

      if (selectedCampaign?.id) {
        await finalizeCampaignCreation(userId, selectedCampaign.id, session);
      }

      return userCredential.user;
    } else {
      trackSentryError('❌ handleRedirectResult: error', { error });
      console.error('❌ handleRedirectResult: error', error);
      throw error;
    }
  }
}

export async function createAnonymousUserAsync() {
  const auth = await getFirebaseAuthAsync();

  const isSignedIn = await isUserSignedInAsync();
  const isAnonymous = await isUserAnonymousAsync();
  const isRegistered = await isUserRegisteredAsync();

  if (isRegistered || (isSignedIn && isAnonymous)) {
    return auth.currentUser;
  } else if (isSignedIn && isAnonymous) {
    return auth.currentUser;
  } else if (!isSignedIn) {
    return (await signInAnonymously(auth)).user;
  } else {
    console.error('signInUserAnonymously: unexpected state');
    return null;
  }
}

export async function hashValue(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

// Helper function to check if we're currently in a redirect flow
export function isInRedirectFlow(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const currentUrl = window.location.href;

  const hasGoogleAuthParams =
    urlParams.has('authuser') || urlParams.has('code') || urlParams.has('state');
  const hasFirebaseAuthParams = urlParams.has('apiKey') || urlParams.has('authDomain');
  const isAuthCallbackUrl =
    window.location.pathname.includes('/__/auth/') ||
    window.location.hash.includes('access_token') ||
    window.location.hash.includes('id_token');
  const hasAuthEventId = urlParams.has('eventId');
  const hasProviderId = urlParams.has('providerId');
  const isFirebaseRedirect =
    currentUrl.includes('firebaseapp.com/__/auth/handler') ||
    hasAuthEventId ||
    hasProviderId;

  const result =
    hasGoogleAuthParams ||
    hasFirebaseAuthParams ||
    isAuthCallbackUrl ||
    isFirebaseRedirect;
  return result;
}

// Function to check if we should expect a redirect result based on recent auth activity
export function shouldExpectRedirectResult(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const hasState = urlParams.has('state');
  const hasCode = urlParams.has('code');
  const hasAuthUser = urlParams.has('authuser');

  const hasRedirectMarker =
    typeof window !== 'undefined' &&
    (!!sessionStorage.getItem('firebase:redirectUser') ||
      !!sessionStorage.getItem('firebase:pendingRedirect') ||
      !!localStorage.getItem('firebase:authUser')); // Checking localStorage for authUser as a fallback

  return hasState || hasCode || hasAuthUser || hasRedirectMarker;
}
